import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Info, Loader2, Wand2 } from "lucide-react";
import React from "react";

import { useAiFillField } from "~/lib/actions/ai-fill-field";
import { cn } from "~/lib/cn";

import { Label } from "./label";
import { PrivateIcon } from "./private-icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/** Small red error line under a field. */
export const ErrorMessage = ({ error }: { error?: string }) => (
  <>{error && <div className="text-xs text-red-600">{error}</div>}</>
);

/** Info icon that shows help text in a tooltip. */
export const HelpText = React.memo(({ text }: { text: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Info className="text-text-tertiary size-[1em] cursor-help text-xs" />
    </TooltipTrigger>
    <TooltipContent>{text}</TooltipContent>
  </Tooltip>
));

/** Label, help tooltip, input slot and error text for one form field. */
export const FormField = ({
  label,
  labelSize = "sm",
  required,
  helpText,
  error,
  checkbox,
  isPrivate,
  fullWidth,
  maxLength,
  onAiFillField,
  inputId,
  children,
}: {
  label?: string;
  labelSize?: "sm" | "md" | "lg" | "xl";
  required?: boolean;
  helpText?: string;
  error?: string;
  checkbox?: boolean;
  isPrivate?: boolean;
  fullWidth?: boolean;
  maxLength?: number;
  onAiFillField?: (value: string) => void;
  /**
   * The control's id, so the label points at it. Without it the id is derived
   * from the label, which repeats when a form renders the same field twice.
   */
  inputId?: string;
  children?: React.ReactNode;
}) => {
  const aiFillFieldMutation = useMutation({
    mutationFn: useAiFillField,
  });

  return (
    <div
      className={cn("flex gap-1", {
        "flex-1": fullWidth,
        "flex-col": !checkbox,
        "flex-row-reverse justify-end": checkbox,
      })}
    >
      {label && (
        <div
          className={cn("flex items-center gap-2", {
            "mb-4": labelSize === "xl",
          })}
        >
          {isPrivate && <PrivateIcon className="size-[1em] text-xs" />}
          <Label
            htmlFor={inputId ?? label.toLowerCase().replace(/\s/g, "-")}
            size={labelSize}
            className={cn("text-text-primary", {
              "text-red-600": error,
            })}
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </Label>
          {helpText && <HelpText text={helpText} />}
          {onAiFillField && (
            <Tooltip>
              <TooltipTrigger
                className="text-text-tertiary hover:text-text-secondary flex h-6 w-6 shrink-0 grow-0 items-center justify-center rounded-full text-sm hover:bg-gray-100"
                disabled={aiFillFieldMutation.isPending}
                type="button"
                onClick={async () => {
                  const result = await aiFillFieldMutation.mutateAsync({
                    data: {
                      label,
                      helpText,
                      maxLength,
                    },
                  });
                  onAiFillField?.(result.content);
                }}
              >
                {aiFillFieldMutation.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : aiFillFieldMutation.isError ? (
                  <AlertTriangle />
                ) : (
                  <Wand2 />
                )}
              </TooltipTrigger>
              <TooltipContent>
                Generate content for this field using AI
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
      <div className="flex items-start justify-stretch gap-2">
        <div className="grow">
          {children}
          <ErrorMessage error={error} />
        </div>
      </div>
    </div>
  );
};
